export default {
    name: "Dashboard",
    data() {
        return {
            activeTab: 'principal',
            tabs: [
                { id: 'principal', value: 'Principal', active: true },
                { id: 'uso', value: 'Uso', active: false },
                { id: 'churn', value: 'Churn', active: false },
            ],
            // Loading states
            loadingGeneral: false,
            loadingUsage: false,
            loadingChurn: false,
            // General tab data
            generalData: null,
            monthlySignupsChart: { labels: [], values: [] },
            professionalTypeChart: { labels: [], datasets: [] },
            subscribersTypeChart: { labels: [], values: [] },
            gatewayChart: { labels: [], datasets: [] },
            // Usage tab data
            usageData: null,
            recipeTypeChart: { labels: [], values: [] },
            monthlyRecipesChart: { labels: [], values: [] },
            doctorUsageChart: { labels: [], values: [] },
            // Churn tab data
            churnData: null,
            monthlyCancellationsChart: { labels: [], values: [] },
            churnRateChart: { labels: [], datasets: [] },
            churnCohortChart: { labels: [], values: [] },
        }
    },
    mounted() {
        this.loadGeneralData();
    },
    methods: {
        handleTabClicked(id) {
            this.activeTab = id;
            this.tabs = this.tabs.map(tab => ({
                ...tab,
                active: tab.id === id
            }));

            // Load data for the selected tab if not already loaded
            if (id === 'uso' && !this.usageData) {
                this.loadUsageData();
            }
            if (id === 'churn' && !this.churnData) {
                this.loadChurnData();
            }
        },
        async loadGeneralData() {
            this.loadingGeneral = true;
            try {
                const res = await this.$userService.dashboardGeneral();
                this.generalData = res.data;
                this.processChartData();
            } catch (error) {
                console.log('Erro ao carregar dashboard', error);
            } finally {
                this.loadingGeneral = false;
            }
        },
        async loadUsageData() {
            this.loadingUsage = true;
            try {
                const res = await this.$userService.dashboardUsage();
                this.usageData = res.data;
                this.processUsageChartData();
            } catch (error) {
                console.log('Erro ao carregar dados de uso', error);
            } finally {
                this.loadingUsage = false;
            }
        },
        async loadChurnData() {
            this.loadingChurn = true;
            try {
                const res = await this.$userService.dashboardChurn();
                this.churnData = res.data;
                this.processChurnChartData();
            } catch (error) {
                console.log('Erro ao carregar dados de churn', error);
            } finally {
                this.loadingChurn = false;
            }
        },
        processChartData() {
            if (!this.generalData) return;

            // Monthly signups chart
            if (this.generalData.monthly_signups) {
                this.monthlySignupsChart = {
                    labels: this.generalData.monthly_signups.map(item => item.month),
                    values: this.generalData.monthly_signups.map(item => item.count),
                };
            }

            // Professional type chart (stacked bar)
            if (this.generalData.signups_by_professional_type) {
                const data = this.generalData.signups_by_professional_type;
                const colors = {
                    doctor: '#2e4664',
                    student: '#4a7c59',
                    dentist: '#f4a261',
                    other: '#e76f51',
                };
                const labels = {
                    doctor: 'Médicos',
                    student: 'Estudantes',
                    dentist: 'Dentistas',
                    other: 'Outros',
                };
                this.professionalTypeChart = {
                    labels: data.map(item => item.month),
                    datasets: ['doctor', 'student', 'dentist', 'other'].map(type => ({
                        label: labels[type],
                        data: data.map(item => item[type] || 0),
                        backgroundColor: colors[type],
                    })),
                };
            }

            // Subscribers by type chart (pie)
            if (this.generalData.subscribers_by_type) {
                this.subscribersTypeChart = {
                    labels: this.generalData.subscribers_by_type.map(item => item.type),
                    values: this.generalData.subscribers_by_type.map(item => item.count),
                };
            }

            // Gateway chart (stacked bar)
            if (this.generalData.monthly_subscriptions_by_gateway) {
                const data = this.generalData.monthly_subscriptions_by_gateway;
                this.gatewayChart = {
                    labels: data.map(item => item.month),
                    datasets: [
                        {
                            label: 'Stripe',
                            data: data.map(item => item.stripe || 0),
                            backgroundColor: '#2e4664',
                        },
                        {
                            label: 'In-App Purchase',
                            data: data.map(item => item.iap || 0),
                            backgroundColor: '#4a7c59',
                        },
                    ],
                };
            }
        },
        processUsageChartData() {
            if (!this.usageData) return;

            // Recipe type chart (pie)
            if (this.usageData.recipe_count_by_type) {
                const typeLabels = {
                    'exam request': 'Pedido de Exame',
                    'simple': 'Simples',
                    'special control': 'Controle Especial',
                    'sick note': 'Atestado',
                    'free text': 'Texto Livre',
                };
                const data = this.usageData.recipe_count_by_type;
                this.recipeTypeChart = {
                    labels: Object.keys(data).map(key => typeLabels[key] || key),
                    values: Object.values(data),
                };
            }

            // Monthly recipes chart (bar)
            if (this.usageData.monthly_recipes) {
                this.monthlyRecipesChart = {
                    labels: this.usageData.monthly_recipes.map(item => item.month),
                    values: this.usageData.monthly_recipes.map(item => item.count),
                };
            }

            // Doctor usage chart (pie)
            if (this.usageData.doctor_subscriber_usage_type) {
                const usageLabels = {
                    'somente_receitas': 'Somente Receitas',
                    'somente_prontuarios': 'Somente Prontuários',
                    'sem_atividade': 'Sem Atividade',
                    'ambas_atividades': 'Ambas Atividades',
                };
                const data = this.usageData.doctor_subscriber_usage_type;
                this.doctorUsageChart = {
                    labels: Object.keys(usageLabels).map(key => usageLabels[key]),
                    values: Object.keys(usageLabels).map(key => data[key] || 0),
                };
            }
        },
        processChurnChartData() {
            if (!this.churnData) return;

            // Monthly cancellations chart (bar)
            if (this.churnData.monthly_stripe_cancellations) {
                const data = this.churnData.monthly_stripe_cancellations;
                this.monthlyCancellationsChart = {
                    labels: data.map(item => item.month),
                    values: data.map(item => item.count),
                };
            }

            // Churn rate chart (line) - 3 lines: churn %, total, churned
            if (this.churnData.monthly_stripe_churn_rate) {
                const data = this.churnData.monthly_stripe_churn_rate;
                this.churnRateChart = {
                    labels: data.map(item => item.month),
                    datasets: [
                        {
                            label: 'Churn %',
                            data: data.map(item => item.churn_rate || 0),
                            borderColor: '#e76f51',
                            backgroundColor: '#e76f5133',
                            tension: 0.3,
                            yAxisID: 'y1',
                        },
                        {
                            label: 'Total',
                            data: data.map(item => item.total || 0),
                            borderColor: '#4a7c59',
                            backgroundColor: '#4a7c5933',
                            tension: 0.3,
                            yAxisID: 'y',
                        },
                        {
                            label: 'Churned',
                            data: data.map(item => item.churned || 0),
                            borderColor: '#2e4664',
                            backgroundColor: '#2e466433',
                            tension: 0.3,
                            yAxisID: 'y',
                        },
                    ],
                };
            }

            // Churn cohort chart (bar) - tempo até cancelar
            if (this.churnData.stripe_churn_cohort) {
                const data = this.churnData.stripe_churn_cohort;
                this.churnCohortChart = {
                    labels: data.map(item => `${item.months_active} ${item.months_active === 1 ? 'mês' : 'meses'}`),
                    values: data.map(item => item.count),
                };
            }
        },
    }
};
